import { removeTrack, updateTrack, type Track } from '@ve/core';
import { Eye, EyeOff, Lock, LockOpen, Trash2, Volume2, VolumeX } from 'lucide-react';
import { memo } from 'react';
import { openContextMenu } from '../../components/ContextMenu.tsx';
import { NumberField } from '../../components/NumberField.tsx';
import { edit, gesture } from '../../state/editor-store.ts';
import { tip } from '../../lib/tooltip.ts';

export const TrackHeader = memo(function TrackHeader({ track, height }: { track: Track; height: number }) {
  const toggle = (key: 'muted' | 'solo' | 'locked' | 'hidden', label: string) =>
    edit(label, (p) => updateTrack(p, track.id, { [key]: !track[key] }));

  return (
    <div
      className={`track-header track-header-${track.kind}`}
      style={{ height }}
      onContextMenu={(e) => {
        e.preventDefault();
        openContextMenu(e.clientX, e.clientY, [
          {
            label: 'Rename track…',
            description: 'Give this track a new name',
            onSelect: () => {
              const name = prompt('Track name', track.name);
              if (name?.trim()) edit('Rename track', (p) => updateTrack(p, track.id, { name: name.trim() }));
            },
          },
          {
            label: 'Delete track',
            description: 'Delete the track and all clips on it',
            danger: true,
            onSelect: () => edit('Delete track', (p) => removeTrack(p, track.id)),
          },
        ]);
      }}
    >
      <div className="track-top">
        <div className="track-name" {...tip(`${track.name} — right-click to rename or delete`)}>
          {track.name}
        </div>
        {track.kind === 'audio' && (
          <div className="track-volume" {...tip('Track volume — drag to adjust, click to type')}>
            <NumberField
              value={track.volume}
              step={0.01}
              min={0}
              max={4}
              displayScale={100}
              unit="%"
              onGestureStart={() => gesture.begin('Track volume')}
              onGestureEnd={() => gesture.commit()}
              onChange={(v) => edit('Track volume', (p) => updateTrack(p, track.id, { volume: v }))}
            />
          </div>
        )}
      </div>
      <div className="track-buttons">
        {track.kind === 'video' && (
          <button
            className={`btn ghost small icon${track.hidden ? ' off' : ''}`}
            {...tip(track.hidden ? 'Show track' : 'Hide track from the picture')}
            onClick={() => toggle('hidden', 'Toggle track visibility')}
          >
            {track.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        )}
        <button
          className={`btn ghost small icon${track.muted ? ' off' : ''}`}
          {...tip(track.muted ? 'Unmute track' : 'Mute track audio')}
          onClick={() => toggle('muted', 'Toggle mute')}
        >
          {track.muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
        </button>
        <button
          className={`btn ghost small icon solo${track.solo ? ' on' : ''}`}
          {...tip(track.solo ? 'Unsolo' : 'Solo — hear only soloed tracks')}
          onClick={() => toggle('solo', 'Toggle solo')}
        >
          S
        </button>
        <button
          className={`btn ghost small icon${track.locked ? ' on' : ''}`}
          {...tip(track.locked ? 'Unlock track' : 'Lock track against edits')}
          onClick={() => toggle('locked', 'Toggle lock')}
        >
          {track.locked ? <Lock size={13} /> : <LockOpen size={13} />}
        </button>
        <button
          className="btn ghost small icon track-delete"
          {...tip('Delete track and its clips')}
          onClick={() => edit('Delete track', (p) => removeTrack(p, track.id))}
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
});
